import { Injectable } from '@angular/core';
import {
  firstValueFrom,
  forkJoin,
  from,
  map,
  of,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import { AiCreditsModalComponent } from 'src/app/ai-chatbot/components/ai-credits-modal/ai-credits-modal.component';
import {
  AICredits,
  AICreditsApiService,
} from 'src/app/api/services/ai-credits-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';

@Injectable({
  providedIn: 'root',
})
export class AiCreditsService {
  private readonly initPromise: Promise<void>;
  private creditsUsedSubject = new Subject<number>();
  private closeOverlaySubject = new Subject<void>();

  closeOverlay$ = this.closeOverlaySubject.asObservable();
  creditsUsed$ = this.creditsUsedSubject.asObservable().pipe(
    switchMap((usedCredits) => {
      return forkJoin([of(usedCredits), from(this.getAICredits())]);
    }),
    map(([usedCredits, credits]) => {
      return {
        usedCredits,
        creditsRemaining: credits.creditsRemaining,
        creditsTotal: credits.creditsTotal,
      };
    }),
  );

  constructor(
    private aiCreditsApiService: AICreditsApiService,
    private modalService: ModalService,
    private currentPlanService: CurrentPlanService,
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
  ) {}

  getAICredits(): Promise<
    Pick<
      AICredits,
      'creditsRemaining' | 'creditsTotal' | 'planSubscriptionCreditsTotal'
    >
  > {
    return this.aiCreditsApiService.getAICredits().then((response) => {
      const { item } = response;
      return {
        creditsRemaining:
          item?.creditsRemaining >= 0 ? item?.creditsRemaining : 0,
        creditsTotal: item?.creditsTotal || 0,
        planSubscriptionCreditsTotal: item?.planSubscriptionCreditsTotal || 0,
      };
    });
  }

  showCreditsUsed(creditsUsed: number): void {
    this.creditsUsedSubject.next(creditsUsed);
  }

  closeOverlay(): void {
    this.closeOverlaySubject.next();
  }

  sendReport(): Promise<void> {
    return this.modalService
      .confirm(
        'Export AI Credit Activity?',
        'An export file will be prepared and emailed to you at <b>' +
          this.userStateService.getUserEmail() +
          '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.',
        'Export',
        'Cancel',
      )
      .then(() => {
        return this.aiCreditsApiService.exportCreditsReport();
      })
      .then(() => {
        this.modalService.showMessage(
          'Export initiated',
          'The AI credit activity export has been initiated. You will receive an email once it is ready.',
        );
      })
      .catch((error) => {
        this.modalService.showMessage(
          'Error',
          error.message || 'Unable to export at this time.',
        );
      });
  }

  async openCreditsModal(): Promise<null> {
    return firstValueFrom(
      this.modalService
        .showCustomDialog(
          AiCreditsModalComponent,
          {
            canModifyPlan: this.currentPlanService.canModifyPlan,
            hasPlan: this.currentPlanService.isSubscribed(),
          },
          {
            width: '1000px',
            maxWidth: '95vw',
            panelClass: ['ai-credits-modal-panel'],
          },
        )
        .afterClosed()
        .pipe(take(1)),
    );
  }

  canUserUseAI(): boolean {
    return this.userStateService.getUserSettings().aiEnabled;
  }

  canUseAi(): boolean {
    return this.canUserUseAI() && this.companyStateService.isAiEnabled();
  }
}
